import React, { useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { Tooltip } from 'primereact/tooltip';
import { OverlayPanel } from 'primereact/overlaypanel';
import { TabView, TabPanel } from 'primereact/tabview';
import {
  Briefcase,
  FileSpreadsheet,
  Mail,
  MessageCircle,
  MessageSquareMore,
  NotebookPen,
  Store,
  UserCog,
} from 'lucide-react';

import styles from './css/tabs.module.css';

/**
 * NotificationDropdownWithTabs
 * @param {Object} props
 * @param {string} icon - Icon class for the button
 * @param {string} label - Tooltip label
 * @param {Array<{label: string, content: React.ReactNode, count?: number}>} tabs - Tab definitions
 * @param {number} notificationsLength - Total notification count (for badge)
 * @param {string} [className] - Optional className for the button
 * @type {Record<string, React.ReactNode>}
 */

const TAB_ICONS = {
  Installer: <UserCog size={20} className="text-orange-500" />,
  Store: <Store size={20} className="text-green-500" />,
  Project: <Briefcase size={20} className="text-red-500" />,
  Email: <Mail size={20} className="text-purple-500" />,
  Message: <MessageCircle size={20} className="text-primary-500" />,
  CRM: <FileSpreadsheet size={20} className="text-pink-500" />,
  Chats: <MessageSquareMore size={20} className="text-green-500" />,
  Notes: <NotebookPen size={20} className="text-orange-500" />,
};

const NotificationDropdownWithTabs = ({
  icon,
  label,
  tabs = [],
  notificationsLength = 0,
  className = '',
  severity,
}) => {
  const op = useRef(null);
  const buttonRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div className={`${styles.pullDownButtons} flex align-items-center`}>
      <Button
        icon={icon}
        text
        ref={buttonRef}
        onClick={e => op.current.toggle(e)}
        className={`${className}`}
        pt={{
          badge: {
            className: 'p-0 absolute ml-4 mb-3',
          },
          root: {
            className: `focus:shadow-none relative p-2 w-3rem sm:w-2rem xl:w-3rem p-2 sm:p-1 xl:p-2`,
          },
          menu: {
            className: `max-h-7rem overflow-auto`,
          },
          icon: {
            className: `text-lg`,
          },
        }}
        severity={severity}
      />

      <i
        className="p-text-secondary p-overlay-badge"
        data-pr-tooltip={
          notificationsLength === undefined ? 0 : notificationsLength
        }
        data-pr-position="right"
        data-pr-at="right top"
        data-pr-my="left center-2"
      >
        {notificationsLength > 0 && (
          <span
            className="red-animated-dot"
            style={{ top: '-20px', left: '-4px' }}
          />
        )}
      </i>
      <Tooltip
        target={buttonRef}
        content={`${label}`}
        position="top"
        className="custom-tooltip text-sm"
      />
      <OverlayPanel
        ref={op}
        className="ml-2 w-11 sm:w-8 md:w-6 xl:w-25rem"
        contentClassName="p-0"
        dismissable
        pt={{
          content: {
            className: 'p-0',
          },
          root: {
            className: 'iconOverlayPanel',
          },
        }}
      >
        <div className={`${styles.notificationTabs}`}>
          <h5 className="m-0 py-2 px-3 pf-surface-primary border-bottom-1 border-200 border-round-top-md flex justify-content-between align-items-center">
            Notifications
            <i
              className="pi pi-times cursor-pointer text-sm"
              onClick={() => op.current?.hide()}
            />
          </h5>
          <TabView
            activeIndex={activeIndex}
            onTabChange={e => setActiveIndex(e.index)}
            className="py-1 pf-tab-view overflow-auto"
            panelContainerClassName="p-0"
            pt={{
              navcontent: {
                className: 'p-1',
              },
              nav: {
                className: 'p-2 gap-1',
              },
            }}
          >
            {tabs.map((tab, idx) => (
              <TabPanel
                key={`${idx}-${tab.label}`}
                header={
                  <div className="flex align-items-center">
                    {TAB_ICONS[tab.label]}

                    <span className="pl-2 text-color">{tab.label}</span>
                    {tab.count > 0 && (
                      <Badge
                        value={tab.count}
                        severity="danger"
                        className="ml-2"
                        style={{ verticalAlign: 'middle' }}
                      />
                    )}
                  </div>
                }
                className="p-0"
                contentClassName="p-0"
                pt={{
                  headeraction: {
                    className:
                      'justify-content-center border-primary-500 bg-white',
                  },
                  headertitle: {
                    className: 'text-primary-500 text-xs font-light',
                  },
                }}
              >
                <div
                  style={{ maxHeight: '50vH', overflowY: 'auto' }}
                  className={`text-sm`}
                >
                  {tab.content}
                </div>
              </TabPanel>
            ))}
          </TabView>
        </div>
      </OverlayPanel>
    </div>
  );
};

export default NotificationDropdownWithTabs;
